import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import "./style/NewEventModal.css";

// Priority: Brit Milah > Wedding > Bar Mitzvah > Bat Mitzvah
// type_id mapping: 1=wedding, 2=bar mitzvah, 3=bat mitzvah, 5=brit milah
function detectEventType(name) {
  const n = name.trim().toLowerCase();
  if (n.includes("brit mila") || name.includes("ברית מילה")) return 5;
  if (n.includes("&")) return 1;
  if (n.includes("bm") || n.includes("bar mit")) return 2;
  if (n.includes("bat mit")) return 3;
  return null;
}

const getEmptyForm = (userId) => ({
  name: "",
  id_name: "",
  date: new Date().toISOString().split("T")[0],
  city: "",
  country: "",
  type_id: "",
  event_room: "",
  user_id: userId ?? null,
});

const NewEventModal = ({ show, onHide, onCreate, user, typeList }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(getEmptyForm(user?.user_id));
  const [errors, setErrors] = useState({});
  const [autoLabel, setAutoLabel] = useState(null); // detected type name for badge
  const selectRef = useRef(null);

  // Reset form each time the modal opens
  useEffect(() => {
    if (show) {
      setForm(getEmptyForm(user?.user_id));
      setErrors({});
      setAutoLabel(null);
    }
  }, [show, user]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = show ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [show]);

  if (!show) return null;

  const handleNameChange = (e) => {
    const name = e.target.value;
    const detectedTypeId = detectEventType(name);

    setForm((prev) => {
      const next = {
        ...prev,
        name,
        id_name: name.replaceAll(" ", "_"),
      };
      if (detectedTypeId !== null) {
        next.type_id = detectedTypeId;
      }
      return next;
    });

    if (detectedTypeId !== null) {
      const match = typeList.find((t) => t.type_id === detectedTypeId);
      setAutoLabel(match?.type_name ?? null);

      // Flash animation on select element
      if (selectRef.current) {
        selectRef.current.classList.remove("nem-type-flash");
        // Force reflow to restart animation
        void selectRef.current.offsetWidth;
        selectRef.current.classList.add("nem-type-flash");
        setTimeout(() => {
          if (selectRef.current) selectRef.current.classList.remove("nem-type-flash");
        }, 400);
      }
    } else {
      setAutoLabel(null);
    }
  };

  const handleTypeChange = (e) => {
    setAutoLabel(null); // manual override clears badge
    setForm((prev) => ({ ...prev, type_id: e.target.value ? parseInt(e.target.value) : "" }));
  };

  const handleSubmit = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = t("events_admin.name_required");
    if (!form.date) errs.date = t("events_admin.date_required");
    if (!form.type_id) errs.type_id = t("events_admin.type_required");

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    ReactGA.event({ category: 'photographer', action: 'event_create_complete', label: form.type_id?.toString() });
    onCreate(form);
  };

  return (
    <div className="nem-overlay" onClick={onHide}>
      <div className="nem-card" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="nem-header">
          <span className="nem-title">{t("events_admin.new_title")}</span>
          <button className="nem-close" onClick={onHide} aria-label="Close">✕</button>
        </div>

        {/* Body */}
        <div className="nem-body">

          {/* Event Info */}
          <section className="nem-section">
            <div className="nem-section-label">Event Info</div>

            <div className="nem-field">
              <label className="nem-label">{t("events_admin.name")} *</label>
              <input
                className={`nem-input${errors.name ? " nem-input--error" : ""}`}
                value={form.name}
                onChange={handleNameChange}
                placeholder="e.g. Sarah & David, Bar Mitzvah Josh"
                autoFocus
              />
              {errors.name && <span className="nem-error-msg">{errors.name}</span>}
            </div>

          </section>

          {/* Date & Type */}
          <section className="nem-section">
            <div className="nem-section-label">Date &amp; Type</div>

            <div className="nem-field">
              <label className="nem-label">{t("events_admin.date")} *</label>
              <input
                type="date"
                className={`nem-input${errors.date ? " nem-input--error" : ""}`}
                value={form.date}
                onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
              />
              {errors.date && <span className="nem-error-msg">{errors.date}</span>}
            </div>

            <div className="nem-field">
              <label className="nem-label">
                {t("events_admin.type")} *
                {autoLabel && (
                  <span className="nem-label-badge" key={autoLabel}>
                    auto: {autoLabel}
                  </span>
                )}
              </label>
              <select
                ref={selectRef}
                className={`nem-input${errors.type_id ? " nem-input--error" : ""}`}
                value={form.type_id}
                onChange={handleTypeChange}
              >
                <option value="">{t("events_admin.select_type")}</option>
                {typeList.map((type) => (
                  <option key={type.type_id} value={type.type_id}>
                    {type.type_name}
                  </option>
                ))}
              </select>
              {errors.type_id && <span className="nem-error-msg">{errors.type_id}</span>}
            </div>
          </section>

          {/* Venue */}
          <section className="nem-section">
            <div className="nem-section-label">Venue</div>

            <div className="nem-field">
              <label className="nem-label">{t("events_admin.venue")}</label>
              <input
                className="nem-input"
                value={form.event_room}
                onChange={(e) => setForm((p) => ({ ...p, event_room: e.target.value }))}
              />
            </div>

            <div className="nem-field">
              <label className="nem-label">{t("events_admin.city")}</label>
              <input
                className="nem-input"
                value={form.city}
                onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
              />
            </div>

            <div className="nem-field">
              <label className="nem-label">{t("events_admin.country")}</label>
              <input
                className="nem-input"
                value={form.country}
                onChange={(e) => setForm((p) => ({ ...p, country: e.target.value }))}
              />
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="nem-footer">
          <button className="nem-btn--ghost" onClick={onHide}>
            {t("events_admin.btn_cancel")}
          </button>
          <button className="nem-btn--primary" onClick={handleSubmit}>
            {t("events_admin.btn_create")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewEventModal;
